import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

const gltfLoader = new GLTFLoader()
const assetCache = new Map()

const HRA_CDN =
  'https://media.githubusercontent.com/media/hubmapconsortium/ccf-3d-reference-object-library/main/VH_Female/v1.2'

function assetUrl(filename) {
  const base = process.env.BASE_URL || '/'
  if (base === './' || base === '.') {
    return `/models/anatomy/${filename}`
  }
  const normalized = base.endsWith('/') ? base : `${base}/`
  return `${normalized}models/anatomy/${filename}`
}

export function getModelAssetUrl(filename, cdnName) {
  return assetUrl(filename)
}

export function getModelCdnUrl(cdnName) {
  return `${HRA_CDN}/${cdnName}`
}

export function loadGltfScene(url, fallbackUrl) {
  const tryLoad = (targetUrl) =>
    new Promise((resolve, reject) => {
      gltfLoader.load(
        targetUrl,
        (gltf) => resolve(gltf.scene),
        undefined,
        reject
      )
    })

  const cacheKey = `${url}::${fallbackUrl || ''}`
  if (assetCache.has(cacheKey)) {
    return assetCache.get(cacheKey).then((scene) => scene.clone(true))
  }

  const task = tryLoad(url)
    .catch(() => {
      if (fallbackUrl && fallbackUrl !== url) return tryLoad(fallbackUrl)
      throw new Error(`模型加载失败: ${url}`)
    })
    .then((scene) => {
      assetCache.set(cacheKey, Promise.resolve(scene))
      return scene
    })
    .catch((err) => {
      assetCache.delete(cacheKey)
      throw err
    })

  assetCache.set(cacheKey, task)
  return task.then((scene) => scene.clone(true))
}

function enhanceMaterial(material, tint, blend = 1) {
  if (!material) return material
  const list = Array.isArray(material) ? material : [material]
  const next = list.map((mat) => {
    if (!mat.isMeshStandardMaterial && !mat.isMeshPhysicalMaterial) {
      const baseColor = tint
        ? (mat.color ? mat.color.clone() : new THREE.Color(0xffffff)).lerp(new THREE.Color(tint), blend)
        : (mat.color ? mat.color.clone() : new THREE.Color(0xffffff))
      return new THREE.MeshStandardMaterial({
        color: baseColor,
        map: mat.map || null,
        normalMap: mat.normalMap || null,
        transparent: mat.transparent,
        opacity: mat.opacity > 0 && mat.opacity < 1 ? mat.opacity : 0.96,
        roughness: 0.62,
        metalness: 0.04,
        side: THREE.DoubleSide,
      })
    }
    if (tint) {
      const target = new THREE.Color(tint)
      if (blend >= 1) {
        mat.color.copy(target)
      } else {
        const base = mat.color ? mat.color.clone() : new THREE.Color(0xffffff)
        base.lerp(target, blend)
        mat.color.copy(base)
      }
    }
    mat.roughness = 0.62
    mat.metalness = 0.04
    if (mat.opacity === 1) mat.opacity = 0.98
    mat.transparent = mat.opacity < 1
    mat.side = THREE.DoubleSide
    mat.needsUpdate = true
    return mat
  })
  return Array.isArray(material) ? next : next[0]
}

export function prepareAnatomyRoot(root, options = {}) {
  const { targetSize = 2.4, tint = null } = options

  root.traverse((obj) => {
    if (obj.isMesh) {
      obj.castShadow = true
      obj.receiveShadow = true
      obj.material = enhanceMaterial(obj.material, tint)
    }
  })

  const box = new THREE.Box3().setFromObject(root)
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())

  // 水平居中，底部对齐，避免气管/血管等向上延伸导致模型整体偏上
  root.position.x -= center.x
  root.position.z -= center.z
  root.position.y -= box.min.y
  root.position.y -= size.y * 0.08

  const maxDim = Math.max(size.x, size.y, size.z, 0.001)
  const scale = targetSize / maxDim
  root.scale.setScalar(scale)

  const normalizedBox = new THREE.Box3().setFromObject(root)
  const visualCenter = normalizedBox.getCenter(new THREE.Vector3())
  return {
    box: normalizedBox,
    scale,
    center: visualCenter,
    size,
  }
}

function matchName(name, matchers) {
  return matchers.some((matcher) => {
    if (typeof matcher === 'string') return name.includes(matcher)
    return matcher.test(name)
  })
}

export function buildComponents(root, componentDefs) {
  const meshOwners = new Map()

  root.traverse((obj) => {
    if (!obj.isMesh) return
    let node = obj
    let matchedDef = null
    // 优先匹配最近的节点（更具体的结构），避免整模被父级名称覆盖
    while (node && node !== root) {
      const def = componentDefs.find((item) => matchName(node.name || '', item.matchers))
      if (def) {
        matchedDef = def
        break
      }
      node = node.parent
    }
    if (!matchedDef) return
    if (!meshOwners.has(matchedDef.id)) meshOwners.set(matchedDef.id, [])
    meshOwners.get(matchedDef.id).push(obj)
  })

  return componentDefs
    .map((def) => {
      const objects = meshOwners.get(def.id) || []
      objects.forEach((obj) => {
        obj.userData.componentId = def.id
        obj.userData.componentName = def.name
        obj.userData.isAnatomyPart = true
      })
      return {
        id: def.id,
        name: def.name,
        color: def.color,
        mesh: objects[0] || null,
        objects,
        visible: true,
      }
    })
    .filter((comp) => comp.objects.length)
}

export function applyComponentColors(components, componentDefs, options = {}) {
  const blend = options.colorBlend != null ? options.colorBlend : 0.85
  componentDefs.forEach((def) => {
    if (def.color == null) return
    const comp = components.find((c) => c.id === def.id)
    if (!comp) return
    comp.objects.forEach((obj) => {
      obj.material = enhanceMaterial(obj.material, def.color, blend)
    })
  })
}

export function getComponentAnchor(components, componentId, box, offset = [0, 0.03, 0.14]) {
  const comp = components.find((c) => c.id === componentId)
  if (!comp || !comp.objects.length) return null
  const compBox = new THREE.Box3()
  comp.objects.forEach((obj) => compBox.expandByObject(obj))
  if (compBox.isEmpty()) return null
  const center = compBox.getCenter(new THREE.Vector3())
  const size = compBox.getSize(new THREE.Vector3())
  center.x += offset[0] * size.x
  center.y += offset[1] * size.y
  center.z += offset[2] * size.z
  return center
}

export function resolveAnchor(components, box, spec) {
  if (spec.componentId) {
    const anchor = getComponentAnchor(components, spec.componentId, box, spec.offset || [0, 0.03, 0.14])
    if (anchor) return anchor
  }
  if (spec.relativePosition) return vecFromRelative(box, spec.relativePosition)
  return box.getCenter(new THREE.Vector3())
}

export function setComponentVisibility(components, map) {
  components.forEach((comp) => {
    if (map[comp.id] === undefined) return
    comp.visible = map[comp.id]
    comp.objects.forEach((obj) => {
      obj.visible = map[comp.id]
    })
  })
}

export function getComponentMeshes(components) {
  const meshes = []
  components.forEach((comp) => {
    comp.objects.forEach((obj) => meshes.push(obj))
  })
  return meshes
}

export function vecFromRelative(box, relative) {
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  return new THREE.Vector3(
    center.x + relative[0] * size.x,
    center.y + relative[1] * size.y,
    center.z + relative[2] * size.z
  )
}

const COLORS = {
  lesion: 0x8e44ad,
  lesionGlow: 0xff6b6b,
  normal: 0x27ae60,
}

export function createLesionMarkers(lesionDef, box, components) {
  const position = resolveAnchor(components, box, lesionDef)
  const normal = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 14, 14),
    new THREE.MeshBasicMaterial({
      color: COLORS.normal,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
    })
  )
  normal.renderOrder = 990
  normal.position.copy(position)
  normal.visible = false
  normal.userData = {
    isLesionMarker: true,
    lesionId: lesionDef.id,
    state: 'normal',
  }

  const lesion = new THREE.Mesh(
    new THREE.SphereGeometry(0.075, 14, 14),
    new THREE.MeshBasicMaterial({
      color: COLORS.lesion,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
    })
  )
  lesion.renderOrder = 991
  lesion.position.copy(position)
  lesion.userData = {
    isLesionMarker: true,
    lesionId: lesionDef.id,
    state: 'lesion',
  }

  let ring = null
  if (lesionDef.withRing) {
    ring = new THREE.Mesh(
      new THREE.RingGeometry(0.07, 0.1, 24),
      new THREE.MeshBasicMaterial({
        color: COLORS.lesionGlow,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.65,
        depthTest: false,
      })
    )
    ring.renderOrder = 989
    ring.position.copy(position)
    ring.lookAt(position.clone().add(new THREE.Vector3(0, 0, 1)))
  }

  return { normal, lesion, ring, position }
}

export function createHotspotMarkers(hotspotDefs, box, components, color = 0xfbbf24) {
  return hotspotDefs.map((hs) => {
    const markerGroup = new THREE.Group()
    const position = resolveAnchor(components, box, hs)
    const dot = new THREE.Mesh(
      new THREE.SphereGeometry(hs.lesionId ? 0.038 : 0.026, 10, 10),
      new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.92 })
    )
    dot.renderOrder = 998
    markerGroup.add(dot)
    markerGroup.position.copy(position)

    const target = hs.componentId
      ? getComponentAnchor(components, hs.componentId, box, [0, 0, 0]) ||
        vecFromRelative(box, hs.relativeTarget || [0, 0, 0])
      : vecFromRelative(box, hs.relativeTarget || hs.relativePosition || [0, 0, 0])

    const hotspotUserData = {
      isHotspot: true,
      hotspotId: hs.id,
      label: hs.label,
      cameraTarget: target,
      cameraDistance: hs.distance,
      lesionId: hs.lesionId || null,
    }
    markerGroup.userData = hotspotUserData
    dot.userData = { ...hotspotUserData, isHotspotDot: true }

    return { ...hs, group: markerGroup, pulseMesh: null, target, position }
  })
}

export async function assembleModel(config) {
  const bodyGroup = new THREE.Group()
  bodyGroup.name = 'body'

  if (config.assets.length === 1) {
    const scene = await loadGltfScene(config.assets[0].url, config.assets[0].fallback)
    bodyGroup.add(scene)
  } else {
    const scenes = await Promise.all(
      config.assets.map((asset) => loadGltfScene(asset.url, asset.fallback))
    )
    scenes.forEach((scene) => bodyGroup.add(scene))
  }

  const root = new THREE.Group()
  root.name = config.id
  root.add(bodyGroup)

  const bounds = prepareAnatomyRoot(bodyGroup, { targetSize: config.targetSize || 2.4 })

  const components = buildComponents(bodyGroup, config.componentDefs)
  applyComponentColors(components, config.componentDefs, { colorBlend: config.colorBlend })
  const lesions = []
  const hotspots = []

  ;(config.lesionDefs || []).forEach((def) => {
    const { normal, lesion, ring, position } = createLesionMarkers(def, bounds.box, components)
    root.add(normal, lesion)
    if (ring) root.add(ring)
    const cameraTarget = def.componentId
      ? getComponentAnchor(components, def.componentId, bounds.box, [0, 0, 0]) ||
        vecFromRelative(bounds.box, def.relativeTarget || [0, 0, 0])
      : vecFromRelative(bounds.box, def.relativeTarget || [0, 0, 0])
    lesions.push({
      id: def.id,
      name: def.name,
      description: def.description,
      normalDescription: def.normalDescription,
      lesionDescription: def.lesionDescription,
      position,
      normalMesh: normal,
      lesionMesh: lesion,
      ringMesh: ring,
      cameraTarget,
      cameraDistance: def.cameraDistance,
    })
  })

  const hotspotMarkers = createHotspotMarkers(
    config.hotspotDefs || [],
    bounds.box,
    components,
    config.hotspotColor
  )
  hotspotMarkers.forEach((hs) => {
    root.add(hs.group)
    hotspots.push(hs)
  })

  return {
    id: config.id,
    name: config.name,
    description: config.description,
    root,
    components,
    hotspots,
    lesions,
    bounds,
  }
}
